import React from 'react';
import { Link } from 'react-router-dom';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Paper,
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { getServiceTypeById } from '../../../booking/models/service-type';
import SectionHeading from '../SectionHeading/SectionHeading';
import { headCellSx, headRowSx, tableContainerSx, tableSx } from '../../tableStyles';

// An en dash is the locale-neutral placeholder for a value the row does not have.
const EMPTY_VALUE = '–';

// Maps the raw serviceTypeId to the same product key used in loyalty.rules.products.
const SERVICE_TYPE_PRODUCT_KEY = {
    COLLECTIVE: 'airportShuttle',
    INDIVIDUAL: 'privateIndividual',
    EXCLUSIVE: 'exclusive',
    UPON_REQUEST: 'a2b',
    SHARED_INDIVIDUAL: 'sharedIndividual',
};

// A booking-level row lists all of the booking's distinct service types (the backend
// already deduped and sorted them). Empty/unknown falls back to the en dash.
const serviceTypeLabel = (t, serviceTypeIds) => {
    const labels = (Array.isArray(serviceTypeIds) ? serviceTypeIds : [])
        .map((id) => SERVICE_TYPE_PRODUCT_KEY[getServiceTypeById(id)])
        .filter(Boolean)
        .map((productKey) => t(`loyalty.rules.products.${productKey}`));
    return labels.length ? labels.join(', ') : EMPTY_VALUE;
};

const totalCellSx = { bgcolor: 'grey.50', borderTop: 2, borderTopColor: 'divider', fontWeight: 600 };

const PointsHistory = ({ history, qualifyingPoints }) => {
    const { t } = useTranslation();
    const rows = Array.isArray(history) ? history : [];
    const total = qualifyingPoints ?? 0;

    return (
        <Box>
            <SectionHeading icon={ReceiptLongIcon}>{t('loyalty.history.title')}</SectionHeading>
            <TableContainer component={Paper} variant="outlined" sx={tableContainerSx}>
                <Table sx={tableSx}>
                    <TableHead>
                        <TableRow sx={headRowSx}>
                            <TableCell sx={headCellSx}>{t('loyalty.history.date')}</TableCell>
                            <TableCell sx={headCellSx}>{t('loyalty.history.booking')}</TableCell>
                            <TableCell sx={headCellSx}>{t('loyalty.history.service')}</TableCell>
                            <TableCell align="right" sx={headCellSx}>
                                {t('loyalty.history.points')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('loyalty.history.empty')}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            rows.map((row, i) => (
                                <TableRow key={i}>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {row.date ? moment(row.date).format('DD.MM.YYYY') : EMPTY_VALUE}
                                    </TableCell>
                                    <TableCell>{row.bookingNumber ?? EMPTY_VALUE}</TableCell>
                                    <TableCell>{serviceTypeLabel(t, row.serviceTypeIds)}</TableCell>
                                    <TableCell align="right" sx={{ color: 'primary.main', fontWeight: 600 }}>
                                        {t('loyalty.history.pointsValue', { points: row.points })}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                        {rows.length > 0 && (
                            <TableRow>
                                <TableCell colSpan={3} sx={totalCellSx}>
                                    {t('loyalty.history.total')}
                                </TableCell>
                                <TableCell align="right" sx={{ ...totalCellSx, color: 'primary.main' }}>
                                    {total}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box sx={{ mt: 1.5, textAlign: 'right' }}>
                <Typography component={Link} to="/profile" variant="body2" sx={{ color: 'primary.main' }}>
                    {t('loyalty.history.link')}
                </Typography>
            </Box>
        </Box>
    );
};

export default PointsHistory;
